import * as i0 from '@angular/core';
import { Injectable, InjectionToken, LOCALE_ID, Inject } from '@angular/core';
import * as i2 from '@polaris-customerweb/sharedcomponents.core';
import { WINDOW, provideCoreMocks, WEB_COMPONENT_HTTP_INTERCEPTORS } from '@polaris-customerweb/sharedcomponents.core';
import { once, merge, has, lowerFirst, set } from 'lodash-es';
import { HttpHeaders } from '@angular/common/http';

class ConfigResourceService {
    constructor(http) {
        this.http = http;
        this.appConfigScope = {
            IND: 'portal_ind',
            ORV: 'portal',
            SLG: 'portal_slg',
        };
    }
    getAppConfigOverrides(appSku, serviceUrl) {
        if (!serviceUrl.endsWith('/')) {
            serviceUrl = `${serviceUrl}/`;
        }
        // The web component http client and standard http client interceptors can/do rely on config, so we must use a client with no interceptors.
        // The ext-sku header is the only one we need to manually specify for this request.
        const headers = new HttpHeaders({ 'ext-sku': appSku });
        return this.http.get(`${serviceUrl}appconfig?scope=${this.appConfigScope[appSku]}`, {
            headers,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ConfigResourceService, deps: [{ token: i2.WebComponentNoInterceptHttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ConfigResourceService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ConfigResourceService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i2.WebComponentNoInterceptHttpClient }] });

const ConfigOptions = new InjectionToken('Web component config options');
function provideConfig(options) {
    return [{ provide: ConfigOptions, useValue: options }, ConfigService];
}

class ConfigService {
    constructor(configOptions, locale, window, configResourceService, localStorageService, sessionStorageService) {
        this.window = window;
        this.configResourceService = configResourceService;
        this.localStorageService = localStorageService;
        this.sessionStorageService = sessionStorageService;
        this.apiUrl = {
            connectedVehicle: 'https://connectedvehicle.polariscvc.com',
            etc: 'https://etc.polaris.com',
            featureConnectivity: 'https://features.polariscvc.com',
            geoFileImport: 'https://gpx.polariscvc.com',
            maintenance: 'https://maintenance.polariscvc.com',
            mapData: 'https://mapdata.polariscvc.com',
            messaging: 'https://messaging.polariscvc.com',
            modem: 'https://modem.polariscvc.com',
            remoteOperations: 'https://remoteoperations.polariscvc.com',
            rideCommand: 'https://api.polariscvc.com',
            roles: 'https://roles.polariscvc.com',
            routing: 'https://routing.polariscvc.com',
            search: 'https://search.polariscvc.com',
            subscription: 'https://subscription.polariscvc.com',
        };
        this.appSku = 'ORV';
        this.appStoreUrl = {
            apple: 'https://itunes.apple.com/us/app/polaris-ride-command/id493558794',
            google: 'https://play.google.com/store/apps/details?id=net.weathernation.mobile.orvtrails',
        };
        this.assetCdnUrl = '';
        this.auth = {
            cookieReadDelayMs: 0,
            cookieReadMaxTries: 0,
            token: null,
        };
        this.baseHref = '/';
        this.branchKey = 'key_live_ggi6qId8E2OqZvKyaaZqWamdEEaZ2Sog';
        this.contentful = {
            spaceId: 'tz7eqr7j3nfi',
            token: '1b0efba0bdde451e8c1695fba34c6a247aaa780d44bcfa86ed38817ae8bb7c90',
        };
        this.cultureCode = '';
        this.defaultRiderColor = 'Blue';
        this.displayUpdatesUrl = 'https://rdc-updates.polaris.com';
        this.environment = 'prod';
        this.extClientId = '';
        this.extendedServiceUrl = 'http://www.protectmypolaris.com';
        this.featureFlags = {
            areCheckInContactsEnabled: false,
            areDtcsEnabled: false,
            areGeofencesEnabled: false,
            areInteractiveTrailsEnabled: false,
            areLocationAlertsEnabled: false,
            areMapboxStandardStyleEnabled: false,
            arePrivacySettingsEnabled: false,
            arePublicWaypointsEnabled: false,
            areRideAreasEnabled: false,
            areSponsorsEnabled: false,
            is3dTerrainEnabled: false,
            isGroupRideMessagingEnabled: false,
            isHazardReportingEnabled: false,
            isIgnitionSecurityEnabled: false,
            isLandInfoEnabled: false,
            isLegacyVehicleSubscriptionEnabled: false,
            isMaintenanceScheduleEnabled: false,
            isRideTrackingEnabled: false,
            isRideTypeToggleEnabled: false,
            isVehicleSubscriptionEnabled: false,
        };
        this.httpTimeout = 8;
        this.isEmbedded = true;
        this.logLevels = {};
        this.mapboxToken = 'pk.eyJ1IjoicG9sYXJpcy1yaWRlcngiLCJhIjoiWExuREx5ayJ9.qK0_9TwlruP7fRC1hASJAA';
        this.mapgearsUrl = 'https://ridecommand.evtrails.com';
        this.maxPoiResults = 50;
        this.maxSponsorResults = 1200;
        this.mqtt = {
            alertQos: 1,
            authorizer: 'iot-auth',
            endpoint: 'iot.polariscvc.com',
            remoteCommandQos: 1,
        };
        // breaking-change: [RC-15533] - remove deprecated ownersManualBucket
        /** @deprecated - use displayUpdatesUrl (e.g.: `${displayUpdatesUrl}/manual/en-7/ind`) */
        this.ownersManualBucket = 'https://s3.amazonaws.com/riderx-pidd/manuals';
        this.phoneNumberRegex = '(\\+1)?\\s?\\(?(\\d{3})\\)?[\\s.-]?(\\d{3})[\\s.-]?(\\d{4})';
        this.pollTimers = {
            alerts: 30,
            groupRideMaxInactivePeriod: 120000,
            groupRideStatusItems: 30,
            location: 240,
            notification: 30,
            rideGroupInfo: 4,
        };
        this.rentalsUrl = 'https://adventures.polaris.com';
        this.rideCommandPlusActivateUrl = 'https://ridecommand.polaris.com/en-us/ride-command-plus/#how-to-get-started';
        this.subscriptionManagementUrl = 'https://polaris.com/en-us/account/profile/subscriptions/';
        this.rideCommandUrl = {
            indian: 'https://ridecommand.indianmotorcycle.com',
            offRoad: 'https://ridecommand.polaris.com',
            slingshot: 'https://ridecommand.slingshot.polaris.com',
        };
        this.searchKey = 'f8ca52a5-d516-446d-a299-52af5c543c31';
        this.tileSource = {
            satellite: 'ckv2kok640ozw15lwxabixmrf',
            satelliteSnow: 'ckv2kdkod0opg14roi1p99e7w',
            terrain: 'ckv2kg9rn3tpa14s1ebbr6ijq',
            terrainSnow: 'ckv2k7dmf3h4i15qfpc9epily',
        };
        this.tileSourceV2 = {
            satellite: 'cmafjiy3r00dh01s7a470he9a',
            satelliteSnow: 'cmafs6lyg00am01s8cmih5k1x',
            terrain: 'cm2dn6ijg006q01pd8bqp4she',
            terrainDark: 'cmab9bvdm000s01rzh375hg2h',
            terrainDarkSnow: 'cmahaz4ax00ff01s86l4pd4eg',
            terrainSnow: 'cm9j0ylu0008m01s557917kiv',
        };
        this.weather = {
            clientId: 'FFLBTPaJzHTDZNsOyTgD6',
            clientSecret: 'TwmyGmKJVU2fp3oDd9Uuq0R1DSSYZRWyusqJuO3c',
            hoursPast: 12,
            url: 'https://data.api.xweather.com',
        };
        this.version = '';
        this._siteUrl = '';
        this.isDebugKey = 'rdxIsDebug';
        this.overridesCacheKey = 'appConfigOverrides';
        this.getChromeVersion = once((window) => {
            const ua = window.navigator.userAgent;
            if (!/chrome/i.test(ua) || /edg|edge|opr|opera/i.test(ua)) {
                return 0;
            }
            const match = /chrome\/(\d+\.\d+)/i.exec(ua);
            return match?.[1] ? Number(match[1]) : 0;
        });
        this.getEdgeVersion = once((window) => {
            const ua = window.navigator.userAgent;
            const match = /edg\/(\d+\.\d+)/i.exec(ua);
            return match?.[1] ? Number(match[1]) : 0;
        });
        this.getFirefoxVersion = once((window) => {
            const ua = window.navigator.userAgent;
            if (!/firefox/i.test(ua) || /seamonkey/i.test(ua)) {
                return 0;
            }
            const match = /firefox\/(\d+\.\d+)/i.exec(ua);
            return match?.[1] ? Number(match[1]) : 0;
        });
        this.getIeVersion = once((window) => {
            const ua = window.navigator.userAgent;
            if (!ua.includes('IEMobile') && 'msPointerEnabled' in window.navigator) {
                if (ua.includes('rv:11.')) {
                    return 11;
                }
                else if (ua.includes('MSIE 10.')) {
                    return 10;
                }
            }
            return 0;
        });
        this.getIsTouch = once((document) => {
            try {
                document.createEvent('TouchEvent');
                return true;
            }
            catch {
                return false;
            }
        });
        this.getSafariVersion = once((window) => {
            const userAgent = window.navigator.userAgent;
            const isSafari = /safari/i.test(userAgent) && !/chrome|crios|crmo|android|edge|edg/i.test(userAgent);
            if (!isSafari) {
                return 0;
            }
            const match = /version\/(\d+\.\d+)/i.exec(userAgent);
            return match?.[1] ? Number(match[1]) : 0;
        });
        merge(this, configOptions, { cultureCode: locale });
        if (configOptions.tileSource?.satellite && configOptions.tileSource.terrain) {
            this.tileSource = { ...configOptions.tileSource };
        }
        if (configOptions.tileSourceV2?.satellite && configOptions.tileSourceV2.terrain) {
            this.tileSourceV2 = { ...configOptions.tileSourceV2 };
        }
        // breaking-change: [RC-30653] - remove token from config service
        if (has(configOptions, 'auth.token')) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            // eslint-disable-next-line no-console
            console.warn('Ability to pass an auth token will be removed in v19.0. Please update your code to call setToken ' +
                'in TokenService instead or provide your own TokenService implementation (see using.md)');
        }
        this.initializeDebugState();
        this.getOverridesFromCache();
        this.getOverridesFromService();
    }
    get brand() {
        if (this.appSku === 'IND') {
            return 'indian';
        }
        else if (this.appSku === 'SLG') {
            return 'slingshot';
        }
        return 'offroad';
    }
    get browser() {
        return {
            chromeVersion: this.getChromeVersion(this.window),
            edgeVersion: this.getEdgeVersion(this.window),
            firefoxVersion: this.getFirefoxVersion(this.window),
            // breaking-change: [RC-36128] - remove ieVersion
            /** @deprecated */
            ieVersion: this.getIeVersion(this.window),
            isTouch: this.getIsTouch(this.window.document),
            safariVersion: this.getSafariVersion(this.window),
        };
    }
    get isDebug() {
        return this.localStorageService.get(this.isDebugKey) ?? false;
    }
    set isDebug(isDebug) {
        if (isDebug) {
            this.localStorageService.set(this.isDebugKey, true);
        }
        else {
            this.localStorageService.remove(this.isDebugKey);
        }
    }
    get isDev() {
        return this.environment === 'dev' || this.environment === 'local';
    }
    /** @deprecated - Use isEmbedded */
    get isEpi() {
        return this.isEmbedded;
    }
    get isIndian() {
        return this.appSku === 'IND';
    }
    get isOffRoad() {
        return this.appSku === 'ORV' || this.appSku === 'SNO';
    }
    get isOnRoad() {
        return !this.isOffRoad;
    }
    get isProd() {
        return this.environment === 'prod';
    }
    get isSlingshot() {
        return this.appSku === 'SLG';
    }
    get isStage() {
        return this.environment === 'stage';
    }
    get isTest() {
        return this.environment === 'test';
    }
    get siteUrl() {
        if (!this._siteUrl) {
            this._siteUrl = this.window.location.origin;
        }
        return this._siteUrl;
    }
    set siteUrl(url) {
        this._siteUrl = url;
    }
    getOverridesFromCache() {
        const overrides = this.sessionStorageService.get(this.overridesCacheKey);
        if (typeof overrides === 'object' && overrides !== null) {
            this.processConfigOverrides(overrides);
        }
        // TODO: clean up old app config store -  remove after 2025-08-10
        this.localStorageService.remove('appConfig');
    }
    getOverridesFromService() {
        this.configResourceService.getAppConfigOverrides(this.appSku, this.apiUrl.rideCommand).subscribe((overrides) => {
            if (typeof overrides === 'object') {
                this.processConfigOverrides(overrides);
                this.sessionStorageService.set(this.overridesCacheKey, overrides);
            }
        });
    }
    initializeDebugState() {
        const searchParams = new URLSearchParams(this.window.location.search);
        const newStatus = searchParams.get('rdxdebug');
        if (newStatus !== null) {
            const isDebug = newStatus === 'true';
            this.isDebug = isDebug;
        }
    }
    processConfigOverrides(overrides) {
        const legacyOverrideKeyMapping = {
            portalconfigAreCheckInContactsEnabled: 'portalconfigFeatureFlags_areCheckInContactsEnabled',
            portalconfigAreLocationAlertsEnabled: 'portalconfigFeatureFlags_areLocationAlertsEnabled',
            portalconfigArePrivacySettingsEnabled: 'portalconfigFeatureFlags_arePrivacySettingsEnabled',
            portalconfigCookieReadDelayMs: 'portalconfigAuth_cookieReadDelayMs',
            portalconfigCookieReadMaxTries: 'portalconfigAuth_cookieReadMaxTries',
            portalconfigIsLegacyVehicleSubscriptionEnabled: 'portalconfigFeatureFlags_isLegacyVehicleSubscriptionEnabled',
            portalconfigIsVehicleSubscriptionEnabled: 'portalconfigFeatureFlags_isVehicleSubscriptionEnabled',
        };
        Object.keys(overrides).forEach((key) => {
            let configKey = key;
            if (Object.keys(legacyOverrideKeyMapping).includes(key) && legacyOverrideKeyMapping[key] !== undefined) {
                configKey = legacyOverrideKeyMapping[key];
            }
            const configPath = lowerFirst(configKey.replace(/portalconfig/i, ''));
            const configParts = configPath.split('_');
            if (!has(this, configParts)) {
                // Using console directly because core lib does not have access to ConsoleLogService.
                // eslint-disable-next-line no-console
                console.debug('unrecognized override encountered');
                return;
            }
            set(this, configParts, overrides[key]);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ConfigService, deps: [{ token: ConfigOptions }, { token: LOCALE_ID }, { token: WINDOW }, { token: ConfigResourceService }, { token: i2.LocalStorageService }, { token: i2.SessionStorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ConfigOptions]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: ConfigResourceService }, { type: i2.LocalStorageService }, { type: i2.SessionStorageService }] });

function provideConfigMocks() {
    return [provideCoreMocks()];
}

class SkuInterceptor {
    constructor(configService) {
        this.configService = configService;
    }
    intercept(req, next) {
        if (req.url.includes('polariscvc')) {
            const clone = req.clone({
                headers: req.headers
                    .append('Ext-Sku', this.configService.appSku)
                    .append('Ext-Client-Id', this.configService.extClientId),
            });
            return next.handle(clone);
        }
        return next.handle(req);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SkuInterceptor, deps: [{ token: ConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SkuInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: SkuInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: ConfigService }] });

function provideSkuInterceptor() {
    return [
        {
            multi: true,
            provide: WEB_COMPONENT_HTTP_INTERCEPTORS,
            useClass: SkuInterceptor,
        },
    ];
}

const siteUrlFactory = once((document) => {
    return document.location.origin;
});

/**
 * Generated bundle index. Do not edit.
 */

export { ConfigOptions, ConfigService, SkuInterceptor, provideConfig, provideConfigMocks, provideSkuInterceptor, siteUrlFactory };
